import { call, put, takeEvery } from "redux-saga/effects"

import api from "../../utilities/api"
import * as apiErrorHelpers from "../../helpers/apiErrorHelpers"
import * as sessionHelpers from "../../helpers/sessionHelpers"
import { trackEvent } from "../../utilities/segmentAnalytics"
import { getServiceNameForMap } from "../../helpers/serviceHelpers"
import {
  getBookedAppointmentDateTime,
  getBookingAppointmentTime
} from "../../helpers/segmentHelpers"
import moment from "moment"
import { Date_Format } from "../../constants/years"
import { AUTHENTICATION_TOKEN, MONTH, YEARS } from "../../constants/application"
import { FAILURE, SUCCESS } from "../../constants/notification"
import { orderBookedEvents } from "../../helpers/googleAnalyticsHelpers"

const SERVICE_ORDERS_AGGREGATE_URL = "/orders/aggregate"
const ORDERS_URL = "/orders"
const VEHICLES_URL = "/vehicles"
const SERVICES_URL = "/services"
const LOAD_BASIC_VEHICLE_DETAILS_URL = "/vehicles/basic_details"

// NOTE: Part 2: Submits the initial service request data along with a selected schedule date/time to create the service request on the backend.
export function* createServiceRequest(action) {
  try {
    yield put({
      type: "SERVICE_REQUEST_CREATE_REQUESTED"
    })

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const request = {
      url: ORDERS_URL,
      body: {
        ...action.payload.formData
      }
    }

    const response = yield call(api.utility.post, api.path(request.url), { body: request.body })

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: "SERVICE_REQUEST_CREATE_SUCCEEDED"
    })

    let result = response.body

    let eventData = {}
    if (result) {
      eventData.appointmentDate = getBookedAppointmentDateTime(result.appointment_datetime)
      eventData.appointmentTime = getBookingAppointmentTime(result.appointment_datetime)
      if (result.shop) {
        eventData.shopName = result.shop.name
        eventData.shopAddress = result.shop.address
      }
      if (result.order_services && result.order_services.length > 0) {
        eventData.servicesSelected = result.order_services.map(getServiceNameForMap)
      }
      if (result.vehicle) {
        eventData.vehicleYear = result.vehicle.year
        eventData.vehicleMake = result.vehicle.make
        eventData.vehicleModel = result.vehicle.model
        eventData.vin = result.vehicle.vin
      }
    }
    orderBookedEvents(response.body)
    trackEvent("appointment-requested", eventData)
    const afterRequestData = {
      alert: action.payload.t("serviceRequestCreatedLabel"),
      alertType: SUCCESS
    }

    yield call(action.callback, SUCCESS, afterRequestData)
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({
      type: "SERVICE_REQUEST_CREATE_FAILED"
    })

    const afterRequestData = {
      error: apiErrorHelpers.toString(errors)
    }

    yield call(action.callback, FAILURE, afterRequestData)

    apiErrorHelpers.consoleLog(errors)
  }
} // createServiceRequest()

export function* loadServiceRequest(action) {
  try {
    yield put({ type: "SET_LOADING" })
    yield put({ type: "UNSET_LOADING_ERROR" })

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const vehicles = action.payload.vehicles || []
    if (!action.payload.vehicle || vehicles.length === 0) {
      yield getAllServices()
      yield put({ type: "VEHICLES_LOAD_REQUESTED" })

      const vehiclesRequest = {
        url: VEHICLES_URL,
        body: {}
      }

      const vehiclesResponse = yield call(api.utility.get, api.path(vehiclesRequest.url), {
        body: vehiclesRequest.body
      })

      apiErrorHelpers.catchErrors(vehiclesResponse.body)

      yield put({
        type: "VEHICLES_LOAD_SUCCEEDED",
        payload: {
          vehicles: vehiclesResponse.body
        }
      })
    }

    yield put({ type: "UNSET_LOADING" })
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "LOADING_FAILED" })
    yield put({ type: "SET_LOADING_ERROR" })
    yield put({ type: "UNSET_LOADING" })

    apiErrorHelpers.consoleLog(errors)
  }
} // function* loadServiceRequest

export function* getAllServices() {
  yield put({ type: "SERVICES_LOAD_REQUESTED" })

  const servicesRequest = {
    url: SERVICES_URL,
    body: { basic: true }
  }

  const servicesResponse = yield call(api.utility.get, api.path(servicesRequest.url), {
    body: servicesRequest.body
  })

  apiErrorHelpers.catchErrors(servicesResponse.body)

  yield put({
    type: "SERVICES_LOAD_SUCCEEDED",
    payload: {
      services: servicesResponse.body
    }
  })
}

export function* getAvailableTimes(action) {
  try {
    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })
    const { services, shopId, vehicleId, date, callback } = action.payload

    const serviceDefinitionIds = services.map((service) => {
      return service.service_definition_id || service.id
    })
    let body = {
      vehicle_id: vehicleId,
      service_definition_ids: serviceDefinitionIds,
      date: date,
      days: 1
    }

    const response = yield call(api.utility.get, api.path(`/shops/${shopId}/schedule`), {
      body: body
    })

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: "AVAILABLE_TIMES_LOAD_SUCCEEDED",
      payload: { availableTimesData: response.body }
    })

    yield call(callback)
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield call(action.payload.callback, apiErrorHelpers.toString(errors))

    apiErrorHelpers.consoleLog(errors)
  }
} // getAvailableTimes()

export function* getServicePrices(action) {
  try {
    yield put({ type: "SET_SERVICE_PRICES_LOADING" })

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const servicesRequest = {
      url: SERVICES_URL,
      body: {
        ...action.payload,
        full_prices: true
      }
    }

    const servicesResponse = yield call(api.utility.get, api.path(servicesRequest.url), {
      body: servicesRequest.body
    })

    apiErrorHelpers.catchErrors(servicesResponse.body)

    yield put({
      type: "SERVICE_PRICES_LOAD_SUCCEEDED",
      payload: {
        servicePrices: servicesResponse.body
      }
    })
    yield put({ type: "UNSET_SERVICE_PRICES_LOADING" })
  } catch (errors) {
    // TODO: setup error handling on backend route and then here
    yield put({ type: "UNSET_SERVICE_PRICES_LOADING" })
  }
}

export function* getServiceOrdersAggregate(action) {
  try {
    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    yield put({ type: "SERVICE_COSTS_LOAD_REQUESTED" })

    const serviceCostsRequest = {
      url: SERVICE_ORDERS_AGGREGATE_URL,
      body: {
        start_date: moment().subtract(YEARS, 1).format(Date_Format.YYYY_MM_01),
        end_date: moment().endOf(MONTH).format(Date_Format.YYYY_MM_DD),
        grouping: MONTH
      }
    }

    const serviceCostsResponse = yield call(api.utility.get, api.path(serviceCostsRequest.url), {
      body: serviceCostsRequest.body
    })

    apiErrorHelpers.catchErrors(serviceCostsResponse.body)

    yield put({
      type: "SERVICE_COSTS_LOAD_SUCCEEDED",
      payload: {
        serviceCosts: serviceCostsResponse.body
      }
    })

    yield put({ type: "UNSET_LOADING" })
  } catch (errors) {
    // TODO: setup error handling on backend route and then here
    yield put({ type: "UNSET_SERVICE_PRICES_LOADING" })
  }
}

export function* updateServiceOrderPayment(action) {
  try {
    yield put({
      type: "SERVICE_UPDATE_REQUESTED"
    })

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const request = {
      url: ORDERS_URL + "/" + action.payload.id,
      body: {
        ...action.payload
      }
    }

    const response = yield call(api.utility.put, api.path(request.url), { body: request.body })

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: "SERVICE_UPDATE_SUCCEEDED"
    })

    orderBookedEvents(response.body)

    yield call(action.callback, SUCCESS, afterRequestData)
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({
      type: "SERVICE_REQUEST_CREATE_FAILED"
    })

    const afterRequestData = {
      error: apiErrorHelpers.toString(errors)
    }

    yield call(action.callback, FAILURE, afterRequestData)

    apiErrorHelpers.consoleLog(errors)
  }
}

export default function* serviceRequestSaga() {
  yield takeEvery("SERVICE_REQUEST_CREATE_SAGA", createServiceRequest)
  yield takeEvery("SERVICE_REQUEST_LOAD_SAGA", loadServiceRequest)
  yield takeEvery("SERVICE_REQUEST_GET_AVAILABLE_TIMES_SAGA", getAvailableTimes)
  yield takeEvery("SERVICE_REQUEST_GET_PRICES_SAGA", getServicePrices)
  yield takeEvery("SERVICE_REQUEST_GET_ALL", getAllServices)
  yield takeEvery("SERVICE_REQUEST_ORDERS_AGGREGATE", getServiceOrdersAggregate)
  yield takeEvery("SERVICE_REQUEST_UPDATE_PAYMENT", updateServiceOrderPayment)
}
